import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { fetchInvoicesByJob } from '../api/invoicesApi';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

// Scoped to a job — the key includes `jobId` so caches for different jobs
// stay isolated. Invalidates on UpdateInvoiceTable (create / update / delete /
// scan-commit). No UpdateReceiptTable listener here — invoice attachments
// piggyback on UpdateInvoiceTable via AttachmentCount being a projected field
// on the invoice rows themselves.
//
// `enabled` lets a caller skip the fetch entirely — used for Workers, who
// 403 on the underlying GetInvoicesByJob endpoint (Manager+ policy) and
// shouldn't see the Invoices section at all.
export default function useInvoicesByJob(jobId, enabled = true) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['invoices', jobId],
        queryFn: () => fetchInvoicesByJob(jobId),
        enabled: enabled && jobId != null,
        retry: false,
    });

    useEffect(() => {
        if (jobId == null || !enabled) return undefined;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                accessTokenFactory: () =>
                    window.Clerk.session.getToken({ template: 'marginpal-api' }),
            })
            .withAutomaticReconnect()
            .build();

        const invalidate = () => {
            queryClient.invalidateQueries({ queryKey: ['invoices', jobId] });
            queryClient.invalidateQueries({ queryKey: ['billingUsage'] });
        };

        connection.on('UpdateInvoiceTable', invalidate);
        // Accounting sync state lives on each invoice row, so refresh the list
        // when a push completes / a payment locks a record.
        connection.on('UpdateAccountingStatus', invalidate);

        connection.start().catch(err => console.error('SignalR connection error:', err));

        return () => {
            connection.stop();
        };
    }, [queryClient, jobId, enabled]);

    return query;
}
